/**
 * Check if an itinerary was created within the last 48 hours
 * @param {string|Date} createdAt - Creation date
 * @returns {boolean}
 */
export const isRecentlyCreated = (createdAt) => {
  if (!createdAt) return false;

  const now = new Date();
  const created = new Date(createdAt);
  const hoursDiff = (now - created) / (1000 * 60 * 60);

  return hoursDiff <= 48;
};

/**
 * Format relative time (e.g., "2 hours ago", "yesterday")
 * @param {string|Date} date - Date to format
 * @returns {string}
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';

  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Save scroll position to session storage
 * @param {string} key - Storage key
 * @param {number} position - Scroll position
 */
export const saveScrollPosition = (key, position) => {
  try {
    sessionStorage.setItem(`scroll_${key}`, position.toString());
  } catch (e) {
    // Ignore errors
  }
};

/**
 * Restore scroll position from session storage
 * @param {string} key - Storage key
 * @returns {number|null}
 */
export const restoreScrollPosition = (key) => {
  try {
    const position = sessionStorage.getItem(`scroll_${key}`);
    return position ? parseInt(position, 10) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Clear scroll position from session storage
 * @param {string} key - Storage key
 */
export const clearScrollPosition = (key) => {
  try {
    sessionStorage.removeItem(`scroll_${key}`);
  } catch (e) {
    // Ignore errors
  }
};

/**
 * Generate a URL-friendly slug for an itinerary
 * Format: {days}-days-in-{city-or-country}-{itineraryType}
 * @param {object} itinerary - Itinerary object
 * @returns {string} URL slug (max 80 chars)
 */
export const generateSlug = (itinerary) => {
  if (!itinerary) return '';

  const days = itinerary.durationDays || itinerary.days || 0;

  // Extract city/place from title, placesCovered, or fall back to country
  let location = '';
  if (itinerary.title) {
    const titleMatch = itinerary.title.match(/(?:in|of|for)\s+([^,]+?)(?:\s*[,-]|$)/i);
    if (titleMatch) location = titleMatch[1].trim();
  }
  if (!location && itinerary.placesCovered && Array.isArray(itinerary.placesCovered) && itinerary.placesCovered.length > 0) {
    const first = itinerary.placesCovered[0];
    location = typeof first === 'string' ? first : (first.name || '');
  }
  if (!location) location = itinerary.city || itinerary.country || 'unknown';

  const type = itinerary.itineraryType || 'tour';

  // Build slug: lowercase, replace non-alphanumeric with hyphens, collapse multiples
  const raw = `${days}-days-in-${location}-${type}`;
  const slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug.slice(0, 80);
};
